import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Card from '../components/Card';
import Button from '../components/Button';
import TabList from '../components/TabList';
import { useAuth } from '../context/AuthContext';
import * as complaintsApi from '../services/complaintsApi';
import { formatDate } from '../utils/dateFormat';
import DateInput from '../components/DateInput';

const BORDER = '1px solid var(--color-border)';
const PAGE = 100;
const STATUS_TABS = [
  { key: '', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'resolved', label: 'Resolved' },
];
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' };
const STATUS_COLOR = {
  open: 'var(--color-danger)',
  in_progress: 'var(--color-warning)',
  resolved: 'var(--color-success, #2e9e5b)',
};
const SORTS = [
  { key: 'count_desc', label: 'Most complaints first' },
  { key: 'count_asc', label: 'Fewest complaints first' },
  { key: 'name', label: 'A to Z' },
];

const fmtDate = (d) => (d ? `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}` : '-');
const th = {
  textAlign: 'left', padding: '8px 10px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', borderBottom: BORDER, whiteSpace: 'nowrap',
};
const td = { padding: '7px 10px', fontSize: 'var(--font-size-sm)', borderBottom: BORDER };
const input = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 'var(--font-size-sm)', border: BORDER, borderRadius: 6, background: 'var(--color-surface, #fff)', color: 'inherit',
};

/** WhatsApp message for the production team (opened through wa.me — the admin picks the chat and presses send). */
function waText({
  complaintNo, party, by, items, description,
}) {
  const lines = items.map((it, i) => (
    `${i + 1}) ${it.design} (${it.size || '-'})\n`
    + `   Batch/Shade: ${it.batchShade || '-'} | Box Pack: ${it.boxPack || '-'}\n`
    + `   Dispatched: ${fmtDate(it.date)} | ${it.qty} Box\n`
    + `   Problem: ${it.problemName || '-'}${it.note ? `\n   Note: ${it.note}` : ''}`
  ));
  let text = `*Complaint ${complaintNo || ''}*\nParty: ${party}${by ? `\nRegistered by: ${by}` : ''}\n\n${lines.join('\n\n')}`;
  if (description) text += `\n\nDetails: ${description}`;
  return text;
}
const openWhatsApp = (text) => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener');

function StatusPill({ status }) {
  return (
    <span style={{
      fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '2px 10px', borderRadius: 999, color: '#fff', background: STATUS_COLOR[status] || 'var(--color-text-muted)',
    }}
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function NewComplaint({ onRegistered, isAdmin, problems, reloadProblems }) {
  const [q, setQ] = useState('');
  const [matches, setMatches] = useState([]);
  const [party, setParty] = useState(null);
  const [lines, setLines] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [loadingLines, setLoadingLines] = useState(false);
  const [filter, setFilter] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [picked, setPicked] = useState([]);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [newProblem, setNewProblem] = useState('');
  const [problemMsg, setProblemMsg] = useState(null);
  const seq = useRef(0);

  useEffect(() => {
    if (party || q.trim().length < 2) { setMatches([]); return undefined; }
    const my = seq.current + 1;
    seq.current = my;
    const t = setTimeout(() => {
      complaintsApi.searchParties(q.trim())
        .then((items) => { if (seq.current === my) setMatches(items); })
        .catch(() => { if (seq.current === my) setMatches([]); });
    }, 250);
    return () => clearTimeout(t);
  }, [q, party]);

  const choose = (p) => {
    setParty(p); setQ(p.name); setMatches([]); setPicked([]); setFilter(''); setLimit(PAGE); setError(null); setDone(null);
    setLoadingLines(true);
    complaintsApi.getPartyDispatches(p.id)
      .then((d) => { setLines(d.lines || []); setTruncated(Boolean(d.truncated)); })
      .catch((err) => setError(err.message || 'Could not load dispatches.'))
      .finally(() => setLoadingLines(false));
  };

  const reset = () => {
    setParty(null); setQ(''); setLines([]); setPicked([]); setDescription(''); setFilter(''); setError(null);
  };

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return lines;
    return lines.filter((l) => [l.date, l.design, l.size, l.batchShade, l.boxPack, l.city].join(' ').toLowerCase().includes(f));
  }, [lines, filter]);

  const pickedIds = useMemo(() => new Set(picked.map((p) => p.id)), [picked]);
  const add = (l) => { if (!pickedIds.has(l.id)) setPicked((prev) => [...prev, { ...l, note: '', problemId: '' }]); };
  const remove = (id) => setPicked((prev) => prev.filter((p) => p.id !== id));
  const setField = (id, field, value) => setPicked((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)));
  const incomplete = picked.some((p) => !p.problemId && !p.note.trim());

  const addNewProblem = async () => {
    setProblemMsg(null);
    try {
      await complaintsApi.addProblem(newProblem);
      setNewProblem('');
      setProblemMsg('Problem added to the list.');
      await reloadProblems();
    } catch (err) {
      setProblemMsg(err.message || 'Could not add problem.');
    }
  };

  const submit = async () => {
    setError(null);
    setSaving(true);
    try {
      const nameOf = (id) => {
        const f = problems.find((x) => String(x.id) === String(id));
        return f ? f.name : '';
      };
      const r = await complaintsApi.createComplaint({
        customerId: party.id,
        description,
        items: picked.map((p) => ({ dispatchTxnId: p.id, problemId: p.problemId ? Number(p.problemId) : null, note: p.note })),
      });
      setDone({
        ...r,
        waText: waText({
          complaintNo: r.complaintNo,
          party: party.name,
          items: picked.map((p) => ({
            design: p.design, size: p.size, batchShade: p.batchShade, boxPack: p.boxPack, date: p.date, qty: p.qty, problemName: nameOf(p.problemId), note: p.note,
          })),
          description,
        }),
      });
      setPicked([]); setDescription('');
      if (onRegistered) onRegistered();
    } catch (err) {
      setError(err.message || 'Could not register the complaint.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Card>
        <label htmlFor="cmp-party" style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>Party name</label>
        <div style={{ position: 'relative', marginTop: 6, display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              id="cmp-party"
              style={input}
              value={q}
              placeholder="Type party name (min 2 letters)…"
              autoComplete="off"
              onChange={(e) => { setQ(e.target.value); if (party) { setParty(null); setLines([]); setPicked([]); } }}
            />
            {matches.length > 0 && (
              <ul style={{
                position: 'absolute', zIndex: 5, left: 0, right: 0, top: '100%', margin: 0, padding: 0, listStyle: 'none', background: 'var(--color-surface, #fff)', border: BORDER, borderRadius: 6, maxHeight: 260, overflowY: 'auto', boxShadow: '0 6px 18px rgba(0,0,0,.15)',
              }}
              >
                {matches.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => choose(m)}
                      style={{
                        display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 0, borderBottom: BORDER, background: 'transparent', cursor: 'pointer', color: 'inherit', fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      {m.name}
                      {m.city ? <span style={{ color: 'var(--color-text-muted)' }}>{` · ${m.city}`}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {party && <Button variant="secondary" onClick={reset}>Change</Button>}
        </div>
      </Card>

      {done && (
        <div style={{ margin: '12px 0' }}>
          <p style={{ color: 'var(--color-success, #2e9e5b)', fontWeight: 600, marginBottom: 6 }}>
            {`Complaint ${done.complaintNo} registered. `}
            {done.telegram && done.telegram.sent
              ? 'Admin ne Telegram par message gayo.'
              : `Telegram message nathi gayo (${(done.telegram && done.telegram.reason) || 'unknown'}) — complaint save thai gai chhe.`}
          </p>
          {isAdmin && <Button variant="secondary" onClick={() => openWhatsApp(done.waText)}>Send to WhatsApp</Button>}
        </div>
      )}
      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}

      {party && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: 16, marginTop: 16, alignItems: 'start' }}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <strong>{`${party.name} — dispatched material`}</strong>
              <input style={{ ...input, width: 220 }} placeholder="Filter design / size / batch…" value={filter} onChange={(e) => { setFilter(e.target.value); setLimit(PAGE); }} />
            </div>
            {loadingLines ? <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p> : (
              <div className="table-scroll" style={{ maxHeight: 460, overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Date', 'Design', 'Size', 'Batch/Shade', 'Box Pack', 'Box', ''].map((h) => <th key={h} style={th}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.slice(0, limit).map((l) => (
                      <tr key={l.id} style={{ background: pickedIds.has(l.id) ? 'var(--color-accent-soft, rgba(0,120,255,.08))' : undefined }}>
                        <td style={td}>{fmtDate(l.date)}</td>
                        <td style={td}>{l.design}</td>
                        <td style={td}>{l.size || '-'}</td>
                        <td style={td}>{l.batchShade || '-'}</td>
                        <td style={td}>{l.boxPack || '-'}</td>
                        <td style={td}>{l.qty}</td>
                        <td style={td}>
                          <Button variant="text" disabled={pickedIds.has(l.id)} onClick={() => add(l)}>
                            {pickedIds.has(l.id) ? 'Added' : '+ Add'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {shown.length === 0 && (
                      <tr><td style={{ ...td, color: 'var(--color-text-muted)' }} colSpan={7}>No dispatches found.</td></tr>
                    )}
                  </tbody>
                </table>
                {shown.length > limit && (
                  <div style={{ padding: 8, textAlign: 'center' }}>
                    <Button variant="secondary" onClick={() => setLimit((n) => n + PAGE)}>{`Show more (${shown.length - limit} left)`}</Button>
                  </div>
                )}
                {truncated && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Only the latest dispatches are listed.</p>}
              </div>
            )}
          </Card>

          <Card>
            <strong>{`Complaint box (${picked.length})`}</strong>
            {isAdmin && (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input style={input} placeholder="Add a new problem to the list…" value={newProblem} maxLength={100} onChange={(e) => setNewProblem(e.target.value)} />
                <Button variant="secondary" disabled={newProblem.trim().length < 2} onClick={addNewProblem}>Add problem</Button>
              </div>
            )}
            {problemMsg && <p style={{ fontSize: 'var(--font-size-xs)', margin: '6px 0 0' }}>{problemMsg}</p>}
            {picked.length === 0 && (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                Left list ma thi je design/batch ma problem chhe tena &quot;+ Add&quot; par click karo.
              </p>
            )}
            {picked.map((p) => (
              <div key={p.id} style={{ border: BORDER, borderRadius: 8, padding: 10, marginTop: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontSize: 'var(--font-size-sm)' }}>
                    <strong>{p.design}</strong>
                    {` · ${p.size || '-'}`}
                    <div style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-xs)' }}>
                      {`${fmtDate(p.date)} · Batch/Shade: ${p.batchShade || '-'} · Box Pack: ${p.boxPack || '-'} · ${p.qty} box`}
                    </div>
                  </div>
                  <Button variant="text" onClick={() => remove(p.id)}>Remove</Button>
                </div>
                <select
                  aria-label={`Problem for ${p.design}`}
                  style={{ ...input, marginTop: 8 }}
                  value={p.problemId}
                  onChange={(e) => setField(p.id, 'problemId', e.target.value)}
                >
                  <option value="">Select problem…</option>
                  {problems.map((pr) => <option key={pr.id} value={pr.id}>{pr.name}</option>)}
                </select>
                <textarea
                  style={{ ...input, marginTop: 8, minHeight: 52, resize: 'vertical' }}
                  placeholder="Extra note (optional)"
                  value={p.note}
                  maxLength={1000}
                  onChange={(e) => setField(p.id, 'note', e.target.value)}
                />
              </div>
            ))}
            {picked.length > 0 && (
              <>
                <textarea
                  style={{ ...input, marginTop: 12, minHeight: 70, resize: 'vertical' }}
                  placeholder="General details (optional) — party ni taklif vishe"
                  value={description}
                  maxLength={2000}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <div style={{ marginTop: 12 }}>
                  <Button disabled={saving || incomplete} onClick={submit}>{saving ? 'Registering…' : 'Register Complaint'}</Button>
                  {incomplete && <span style={{ marginLeft: 10, fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>Darek line mate problem select karo.</span>}
                </div>
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function ComplaintList({ isAdmin, refreshKey }) {
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [problems, setProblems] = useState([]);
  const [problemId, setProblemId] = useState('');
  const [sort, setSort] = useState('count_desc');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [resNote, setResNote] = useState('');
  const [busy, setBusy] = useState(false);
  const noteMissing = !resNote.trim();

  const sortedProblems = useMemo(() => {
    const list = [...problems];
    if (sort === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === 'count_asc') list.sort((a, b) => a.count - b.count || a.name.localeCompare(b.name));
    else list.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    return list;
  }, [problems, sort]);

  const load = () => complaintsApi.listComplaints(status, problemId, from, to).then(setItems);

  // Problem counts follow the chosen date range, so the dropdown shows what happened in that period.
  useEffect(() => {
    complaintsApi.listProblems(from, to).then(setProblems).catch(() => {});
  }, [from, to, refreshKey]);

  useEffect(() => {
    setLoading(true);
    load().catch((err) => setError(err.message || 'Could not load complaints.')).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, problemId, from, to, refreshKey]);

  const toggle = (id) => {
    if (openId === id) { setOpenId(null); setDetail(null); return; }
    setOpenId(id); setDetail(null);
    complaintsApi.getComplaint(id)
      .then((d) => { setDetail(d); setResNote(d.resolutionNote || ''); })
      .catch((err) => setError(err.message || 'Could not load complaint.'));
  };

  const change = async (next) => {
    setBusy(true);
    try {
      const d = await complaintsApi.setComplaintStatus(openId, next, resNote);
      setDetail(d);
      await load();
    } catch (err) {
      setError(err.message || 'Could not update status.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <TabList tabs={STATUS_TABS} activeKey={status} onChange={setStatus} ariaLabel="Complaint status" />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '4px 0 14px' }}>
        <select aria-label="Problem filter" style={{ ...input, width: 300 }} value={problemId} onChange={(e) => setProblemId(e.target.value)}>
          <option value="">All problems</option>
          {sortedProblems.map((p) => <option key={p.id} value={p.id}>{`${p.name} (${p.count})`}</option>)}
        </select>
        <select aria-label="Sort problems" style={{ ...input, width: 220 }} value={sort} onChange={(e) => setSort(e.target.value)}>
          {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-sm)' }}>
          From
          <DateInput aria-label="From date" style={{ ...input, width: 150 }} value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--font-size-sm)' }}>
          To
          <DateInput aria-label="To date" style={{ ...input, width: 150 }} value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
        </label>
        {(from || to) && <Button variant="text" onClick={() => { setFrom(''); setTo(''); }}>Clear dates</Button>}
      </div>
      {error && <p style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {loading ? <p style={{ color: 'var(--color-text-secondary)' }}>Loading…</p> : (
        <Card>
          <div className="table-scroll">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>{['No.', 'Date', 'Party', 'Items', 'By', 'Status'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <React.Fragment key={c.id}>
                    <tr onClick={() => toggle(c.id)} style={{ cursor: 'pointer' }}>
                      <td style={td}>{c.complaintNo}</td>
                      <td style={td}>{formatDate(c.createdAt)}</td>
                      <td style={td}>{c.customerName}</td>
                      <td style={td}>{c.itemCount}</td>
                      <td style={td}>{c.createdByName || '-'}</td>
                      <td style={td}><StatusPill status={c.status} /></td>
                    </tr>
                    {openId === c.id && (
                      <tr>
                        <td style={{ ...td, background: 'var(--color-bg-subtle, rgba(0,0,0,.03))' }} colSpan={6}>
                          {!detail ? 'Loading…' : (
                            <div>
                              {detail.items.map((it) => (
                                <div key={it.id} style={{ padding: '6px 0', borderBottom: BORDER }}>
                                  <strong>{it.design}</strong>
                                  {` · ${it.size || '-'} · ${fmtDate(it.date)} · Batch/Shade: ${it.batchShade || '-'} · Box Pack: ${it.boxPack || '-'} · ${it.qty} box`}
                                  <div style={{ color: 'var(--color-text-secondary)' }}>{`Problem: ${it.problemName || '-'}${it.note ? ` — ${it.note}` : ''}`}</div>
                                </div>
                              ))}
                              {detail.description && <p>{`Details: ${detail.description}`}</p>}
                              {isAdmin && (
                                <div style={{ marginTop: 10 }}>
                                  <Button
                                    variant="secondary"
                                    onClick={() => openWhatsApp(waText({
                                      complaintNo: detail.complaintNo, party: detail.customerName, by: detail.createdByName, items: detail.items, description: detail.description,
                                    }))}
                                  >
                                    Send to WhatsApp
                                  </Button>
                                  <input style={{ ...input, marginTop: 10 }} placeholder="Resolution note (required to mark resolved)" value={resNote} onChange={(e) => setResNote(e.target.value)} />
                                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                    <Button variant="secondary" disabled={busy} onClick={() => change('open')}>Open</Button>
                                    <Button variant="secondary" disabled={busy} onClick={() => change('in_progress')}>In progress</Button>
                                    <Button disabled={busy || noteMissing} onClick={() => change('resolved')}>Mark resolved</Button>
                                  </div>
                                </div>
                              )}
                              {!isAdmin && detail.resolutionNote && <p>{`Resolution: ${detail.resolutionNote}`}</p>}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {items.length === 0 && (
                  <tr><td style={{ ...td, color: 'var(--color-text-muted)' }} colSpan={6}>No complaints yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export default function ComplaintsPage() {
  const { user } = useAuth();
  const isAdmin = Boolean(user && user.role && (user.role.code === 'admin' || user.role.code === 'super_admin'));
  const [tab, setTab] = useState('new');
  const [refreshKey, setRefreshKey] = useState(0);
  const [testMsg, setTestMsg] = useState(null);
  const [problems, setProblems] = useState([]);

  const reloadProblems = useCallback(() => complaintsApi.listProblems().then(setProblems).catch(() => {}), []);
  useEffect(() => { reloadProblems(); }, [reloadProblems, refreshKey, tab]);

  const sendTest = async () => {
    try {
      const r = await complaintsApi.telegramTest();
      setTestMsg(r.sent ? 'Test message Telegram par gayo.' : `Telegram nathi gayo: ${r.reason}`);
    } catch (err) {
      setTestMsg(err.message || 'Test failed.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1>Complaints</h1>
          <p style={{ color: 'var(--color-text-secondary)' }}>
            Party select karo, tene gayelo material (design / size / batch) mathi problem wali line add karo, problem select karo ane register karo — admin ne Telegram par message jaay chhe.
          </p>
        </div>
        {isAdmin && <Button variant="secondary" onClick={sendTest}>Send Telegram test</Button>}
      </div>
      {testMsg && <p style={{ fontSize: 'var(--font-size-sm)' }}>{testMsg}</p>}
      <TabList
        tabs={[{ key: 'new', label: 'New Complaint' }, { key: 'list', label: 'All Complaints' }]}
        activeKey={tab}
        onChange={setTab}
        ariaLabel="Complaints view"
      />
      {tab === 'new'
        ? <NewComplaint onRegistered={() => setRefreshKey((n) => n + 1)} isAdmin={isAdmin} problems={problems} reloadProblems={reloadProblems} />
        : <ComplaintList isAdmin={isAdmin} refreshKey={refreshKey} />}
    </div>
  );
}
